import { CellSelection, Rect, TableMap } from '@tiptap/pm/tables';
import { NodeWithPos as NodeWithPosition } from '@tiptap/core';
import { Node as ProsemirrorNode, ResolvedPos } from '@tiptap/pm/model';
import { Selection, TextSelection, EditorState } from '@tiptap/pm/state';
import { FindProsemirrorNodeResult, findParentNodeOfType } from './prosemirror_utils';

/**
 * Finds the nearest parent table node (if it exists)
 */
export function findTable(
   selection: EditorState | Selection | ResolvedPos
): FindProsemirrorNodeResult | undefined {
   return findParentNodeOfType({ selection, types: 'table' });
}

/**
 * Finds the nearest parent table cell or table header node (if it exists)
 */
export function findCell(
   selection: EditorState | Selection | ResolvedPos
): FindProsemirrorNodeResult | undefined {
   return findParentNodeOfType({ selection, types: ['tableCell', 'tableHeader'] });
}

/**
 * Using the provided `selection` to determine context, returns all the
 * nodes in the column corresponding to the provided `columnIndex`
 */
export function getCellsInColumn(
   selection: Selection,
   columnIndex: number
): NodeWithPosition[] | undefined {
   const table = findTable(selection);

   if (!table) {
      return;
   }

   const map = TableMap.get(table.node);

   if (!isInTableMap(0, columnIndex, map)) {
      return;
   }

   const cells = map.cellsInRect({
      left: columnIndex,
      right: columnIndex + 1,
      top: 0,
      bottom: map.height,
   });

   const columnNodes: NodeWithPosition[] = [];
   cells.forEach(nodePos => {
      columnNodes.push({
         node: table.node.nodeAt(nodePos) as ProsemirrorNode,
         pos: table.start + nodePos,
      });
   });

   return columnNodes;
}

/**
 * Using the provided `selection` to determine context, returns all the
 * nodes in the row corresponding to the provided `rowIndex`
 */
export function getCellsInRow(
   selection: Selection,
   rowIndex: number
): NodeWithPosition[] | undefined {
   const table = findTable(selection);

   if (!table) {
      return;
   }

   const map = TableMap.get(table.node);

   if (!isInTableMap(rowIndex, 0, map)) {
      return;
   }

   const cells = map.cellsInRect({
      left: 0,
      right: map.width,
      top: rowIndex,
      bottom: rowIndex + 1,
   });

   const rowNodes: NodeWithPosition[] = [];
   cells.forEach(nodePos => {
      rowNodes.push({
         node: table.node.nodeAt(nodePos) as ProsemirrorNode,
         pos: table.start + nodePos,
      });
   });

   return rowNodes;
}

/**
 * Predicate checking whether the selection is a [[`CellSelection`]].
 *
 * @param value - the value to check
 */
export function isCellSelection(value: unknown): value is CellSelection {
   return typeof value === 'object' && value instanceof CellSelection;
}

/**
 * Predicate checking whether the given `rect` is selected. If `strict` is true,
 * the function checks that the selection is only the rect
 *
 * @param rect - A rectangle of cells in a table
 * @param selection - A selection to compare to the rectangle
 * @param strict - Whether to check that the selection is only the rect
 */
export function isRectSelected(rect: Rect, selection: Selection, options?: { strict?: boolean }) {
   if (!isCellSelection(selection)) {
      return false;
   }

   const table = findTable(selection);

   if (!table) return false;

   const map = TableMap.get(table.node);

   const cells = map.cellsInRect(rect);
   const selectedCells = map.cellsInRect(
      map.rectBetween(
         selection.$anchorCell.pos - table.start,
         selection.$headCell.pos - table.start
      )
   );

   for (const cell of cells) {
      if (!selectedCells.includes(cell)) {
         return false;
      }
   }

   if (options?.strict) {
      for (const selectedCell of selectedCells) {
         if (!cells.includes(selectedCell)) {
            return false;
         }
      }
   }

   return true;
}

/**
 * Checks if entire row at index `rowIndex` is selected.  If `strict` is true,
 * the function checks that the selection is only the row
 */
export function isRowSelected(
   selection: Selection,
   rowIndex: number,
   options?: { strict?: boolean }
) {
   if (isCellSelection(selection)) {
      const table = findTable(selection);
      if (!table) {
         return false;
      }
      const map = TableMap.get(table.node);
      const position = map.positionAt(rowIndex, 0, table.node);
      const resolvedPos = table.node.resolve(position);
      const rowSelection = CellSelection.rowSelection(resolvedPos);
      const rect = map.rectBetween(rowSelection.$anchorCell.pos, rowSelection.$headCell.pos);

      return isRectSelected(rect, selection, options);
   }

   return false;
}

/**
 * Checks if entire column at index `columnIndex` is selected. If `strict` is true,
 * the function checks that the selection is only the column
 */
export function isColumnSelected(
   selection: Selection,
   columnIndex: number,
   options?: { strict?: boolean }
) {
   if (isCellSelection(selection)) {
      const table = findTable(selection);
      if (!table) {
         return false;
      }
      const map = TableMap.get(table.node);
      const position = map.positionAt(0, columnIndex, table.node);
      const resolvedPos = table.node.resolve(position);
      const columnSelection = CellSelection.colSelection(resolvedPos);
      const rect = map.rectBetween(columnSelection.$anchorCell.pos, columnSelection.$headCell.pos);

      return isRectSelected(rect, selection, options);
   }

   return false;
}

/**
 * Checks if the current selection is an entire table
 */
export function isTableSelected(selection: Selection) {
   if (isCellSelection(selection)) {
      const table = findTable(selection);
      if (!table) {
         return false;
      }
      const map = TableMap.get(table.node);

      return isRectSelected(
         {
            left: 0,
            right: map.width,
            top: 0,
            bottom: map.height,
         },
         selection
      );
   }

   return false;
}

/**
 * Checks if the provided `rowIndex` and `columnIndex` pair can
 * exist in the provided `tableMap`
 */
export function isInTableMap(rowIndex: number, columnIndex: number, tableMap: TableMap) {
   return (
      columnIndex >= 0 && // Left boundary
      columnIndex < tableMap.width && // Right boundary
      rowIndex >= 0 && // Top boundary
      rowIndex < tableMap.height // Bottom boundary
   );
}

export function isCursorInColumn(selection: Selection, columnIndex: number): boolean {
   if (!(selection instanceof TextSelection)) {
      return false;
   }

   const table = findTable(selection);
   if (!table) {
      return false;
   }

   const map = TableMap.get(table.node);

   const currentCell = findCell(selection);
   if (!currentCell) {
      return false;
   }

   const currentCellCoordinates = map.findCell(currentCell.pos - table.start);
   return currentCellCoordinates.left === columnIndex;
}
export function isCursorInRow(selection: Selection, rowIndex: number): boolean {
   if (!(selection instanceof TextSelection)) {
      return false;
   }

   const table = findTable(selection);
   if (!table) {
      return false;
   }

   const map = TableMap.get(table.node);

   const currentCell = findCell(selection);
   if (!currentCell) {
      return false;
   }

   const currentCellCoordinates = map.findCell(currentCell.pos - table.start);
   return currentCellCoordinates.top === rowIndex;
}
